import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ViewToggle from "@/components/ViewToggle";
import ErrorDisplay from "@/components/ErrorDisplay";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useAllPppcPolicies,
  useDeletePppcPolicy,
} from "@/hooks/usePppcPolicy";
import { useApplications } from "@/hooks/useApplications";
import { useAppIconLookup } from "@/hooks/useAppIconLookup";
import { PppcPolicyList } from "@/components/applications/pppc/PppcPolicyList";
import { PppcPolicySnapshot } from "@/types/pppc";
import {
  SortControls,
  type SortOption,
} from "@/components/configurations/SortControls";
import {
  usePersistentSort,
  compareSortString,
  toSortTime,
} from "@/hooks/usePersistentSort";

const STORAGE_KEY = "pppcPoliciesViewPreference";
const SORT_STORAGE_KEY = "pppcPoliciesSortPreference";

type SortField = "name" | "app" | "identifier" | "services" | "updated";

const SORT_OPTIONS: ReadonlyArray<SortOption<SortField>> = [
  { value: "updated", label: "Last updated" },
  { value: "name", label: "Policy name" },
  { value: "app", label: "App name" },
  { value: "identifier", label: "Identifier" },
  { value: "services", label: "Services count" },
];

const VALID_FIELDS: SortField[] = SORT_OPTIONS.map((o) => o.value);

/**
 * Count the number of services that are explicitly set (allow or deny). The
 * "unset" choice is treated as no decision so it does not contribute to the
 * sort weight.
 */
function countConfiguredServices(policy: PppcPolicySnapshot): number {
  let n = 0;
  for (const choice of Object.values(policy.choices ?? {})) {
    if (choice === "allow" || choice === "deny") n += 1;
  }
  return n;
}

export default function PppcPoliciesPage() {
  const navigate = useNavigate();
  const { data: policies = [], isLoading, error } = useAllPppcPolicies();
  const { applications } = useApplications();
  const deleteMutation = useDeletePppcPolicy();

  const [view, setView] = useState<"grid" | "list">(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "grid" || saved === "list" ? saved : "list";
  });
  const [search, setSearch] = useState("");
  const [identifierTypeFilter, setIdentifierTypeFilter] = useState<
    "all" | "bundleID" | "path"
  >("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | "linked" | "external">(
    "all",
  );
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const { sort, setField, setDirection } = usePersistentSort<SortField>(
    SORT_STORAGE_KEY,
    { field: "updated", direction: "desc" },
    VALID_FIELDS,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, view);
  }, [view]);

  const linkedBundleIds = useMemo(
    () => new Set(applications.map((a) => a.bundleId).filter(Boolean) as string[]),
    [applications],
  );

  const isLinkedToCatalog = (p: PppcPolicySnapshot) =>
    p.identifierType === "bundleID" && linkedBundleIds.has(p.identifier);

  // Map known catalog apps: bundleId → icon URL.
  const catalogIconByBundleId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const app of applications) {
      if (app.bundleId && app.icon) map[app.bundleId] = app.icon;
    }
    return map;
  }, [applications]);

  // For external bundle-ID policies (no catalog match), resolve icons via iTunes.
  // Path-based identifiers and linked rows are skipped to avoid wasted lookups.
  const externalBundleApps = useMemo(
    () =>
      policies
        .filter(
          (p) =>
            p.identifierType === "bundleID" &&
            !linkedBundleIds.has(p.identifier),
        )
        .map((p) => ({ bundleId: p.identifier })),
    [policies, linkedBundleIds],
  );
  const itunesIconMap = useAppIconLookup(externalBundleApps);

  const resolveIcon = (p: PppcPolicySnapshot): string | null => {
    if (p.identifierType !== "bundleID") return null;
    return (
      catalogIconByBundleId[p.identifier] ||
      itunesIconMap[p.identifier] ||
      null
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (identifierTypeFilter !== "all" && p.identifierType !== identifierTypeFilter) {
        return false;
      }
      if (sourceFilter !== "all") {
        const linked = isLinkedToCatalog(p);
        if (sourceFilter === "linked" && !linked) return false;
        if (sourceFilter === "external" && linked) return false;
      }
      if (!q) return true;
      return (
        (p.appName ?? "").toLowerCase().includes(q) ||
        (p.policyName ?? "").toLowerCase().includes(q) ||
        p.identifier.toLowerCase().includes(q)
      );
    });
  }, [policies, search, identifierTypeFilter, sourceFilter, linkedBundleIds]);

  const sorted = useMemo(() => {
    const dir = sort.direction === "asc" ? 1 : -1;
    // Stable secondary sort by policy name keeps deterministic ordering when
    // primary keys are equal (e.g. several policies with the same service count).
    return [...filtered].sort((a, b) => {
      let primary = 0;
      const nameA = a.policyName ?? a.appName ?? a.identifier;
      const nameB = b.policyName ?? b.appName ?? b.identifier;
      switch (sort.field) {
        case "name":
          primary = compareSortString(nameA, nameB);
          break;
        case "app":
          primary = compareSortString(a.appName ?? "", b.appName ?? "");
          break;
        case "identifier":
          primary = compareSortString(a.identifier, b.identifier);
          break;
        case "services":
          primary = countConfiguredServices(a) - countConfiguredServices(b);
          break;
        case "updated":
          primary = toSortTime(a.updatedAt) - toSortTime(b.updatedAt);
          break;
      }
      if (primary !== 0) return primary * dir;
      return compareSortString(nameA, nameB);
    });
  }, [filtered, sort]);

  const handleView = (p: PppcPolicySnapshot) =>
    navigate(`/configurations/pppc/${p.policyId}`);

  const handleEdit = (p: PppcPolicySnapshot) =>
    navigate(`/configurations/pppc/${p.policyId}/edit`);

  const handleDuplicate = (p: PppcPolicySnapshot) =>
    navigate(`/configurations/pppc/new?duplicate=${p.policyId}`);

  const handleDelete = (p: PppcPolicySnapshot) => setPendingDeleteId(p.policyId);

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    await deleteMutation.mutateAsync(pendingDeleteId);
    setPendingDeleteId(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <ErrorDisplay error={error as Error} />
        </div>
      </div>
    );
  }

  const totalLinked = policies.filter(isLinkedToCatalog).length;
  const totalExternal = policies.length - totalLinked;

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6 space-y-6">
        <PageHeader
          title="Privacy Permissions (PPPC)"
          description="Pre-approve macOS privacy services for any app — even ones not in your catalog."
        >
          <Button asChild>
            <Link to="/configurations/pppc/new">
              <Plus className="mr-2 h-4 w-4" />
              New PPPC Policy
            </Link>
          </Button>
        </PageHeader>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by app, bundle ID, or path…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-72"
              />
            </div>

            <Select
              value={identifierTypeFilter}
              onValueChange={(v) =>
                setIdentifierTypeFilter(v as typeof identifierTypeFilter)
              }
            >
              <SelectTrigger className="w-44" aria-label="Filter by identifier type">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All identifiers</SelectItem>
                <SelectItem value="bundleID">Bundle ID</SelectItem>
                <SelectItem value="path">Path</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={sourceFilter}
              onValueChange={(v) => setSourceFilter(v as typeof sourceFilter)}
            >
              <SelectTrigger className="w-44" aria-label="Filter by source">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="linked">Linked to catalog</SelectItem>
                <SelectItem value="external">External app</SelectItem>
              </SelectContent>
            </Select>

            <SortControls
              field={sort.field}
              direction={sort.direction}
              options={SORT_OPTIONS}
              onFieldChange={setField}
              onDirectionChange={setDirection}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{policies.length}</Badge>
                <span>Total</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{totalLinked}</Badge>
                <span>Linked</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{totalExternal}</Badge>
                <span>External</span>
              </div>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-3">
                <div className="h-5 w-2/3 bg-muted rounded animate-pulse" />
                <div className="h-4 w-full bg-muted rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="border rounded-lg p-12 text-center">
            <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold">
              {policies.length === 0 ? "No PPPC policies yet" : "No matches"}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              {policies.length === 0
                ? "Create one for an app in your catalog or for an external app installed outside of Smplify."
                : "Try a different search term or filter."}
            </p>
            {policies.length === 0 && (
              <Button asChild>
                <Link to="/configurations/pppc/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New PPPC Policy
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <PppcPolicyList
            policies={sorted}
            view={view}
            isLinkedToCatalog={isLinkedToCatalog}
            resolveIcon={resolveIcon}
            onView={handleView}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        )}
      </div>

      <AlertDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete PPPC policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the privacy approvals for this app. Devices
              will revert to user-prompt behavior on next sync.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
